
import Vue from 'vue';
import VueZoomer from 'vue-zoomer';
import moment from 'moment-timezone';
import { storage } from '@/firebase';
import { mapActions, mapState } from 'vuex';
import { ResizeObserver } from 'resize-observer';
import {
	RunnitNodeRunResult,
	RunnitUpload,
	THUMB_SIZE,
	getRunnitUploadFilePath,
	getRunnitNodeRunResultFilePath,
	Avatar,
	getAvatarFilePath,
	AVATAR_SIZE,
	RUNNIT_NODE_RUN_RESULT_TYPE,
	findFieldByFieldDefUuid
} from '@run-diffusion/shared';
import { NODE_RUN_RESULT_SELECTION_MODE } from '@/views/Runnits/constants';
import RunnitImageTimer from '@/views/Runnits/RunnitImageTimer.vue';
import _findIndex from 'lodash/findIndex';
import { SelectionState } from '@/store';
import { RunnitsImageSelectMixin } from '@/mixins/RunnitsImageSelectMixin';
import {
	GALLERY_SELECTION_CONTEXT_KEY,
	isGalleryMultiSelection,
	isGallerySelectionMode,
	isGallerySingleSelection,
	isSelectedInGalleryContext,
} from '@/components/ImageGallery/gallerySelectionContext';

export default Vue.extend({
	name: 'RunnitImage',
	inject: {
		gallerySelectionContext: { from: GALLERY_SELECTION_CONTEXT_KEY, default: null },
	},
	props: {
		timerStartAtMillis: { type: Number, default: null },
		disabled: { type: Boolean, default: false },
		nodeRunResult: { type: Object, default: null },
		nodeRun: { type: Object, default: null },
		upload: { type: Object, default: null },
		avatar: { type: [Object, String], default: null },
		noClick: { type: Boolean, default: false },
		isThumb: { type: Boolean, default: false },
		isAvatar: { type: Boolean, default: false },
		clearable: { type: Boolean, default: false },
		clearText: { type: String, default: 'Clear image' },
		width: { type: [String, Number], default: null },
		height: { type: [String, Number], default: null },
		maxHeight: { type: [String, Number], default: null },
		isErrorPlaceholder: { type: Boolean, default: false },
		isLoadingPlaceholder: { type: Boolean, default: false },
		isInsideInfoCarousel: { type: Boolean, default: false },
		loadEager: { type: Boolean, default: false },
		isSelectedImage: { type: Boolean, default: false },
		allowFullScreen: { type: Boolean, default: false },
		useZoomViewer: { type: Boolean, default: false },
		coldStartWarningSeconds: { type: Number, default: null },
		draggable: { type: Boolean, default: false },
		isDragging: { type: Boolean, default: false },
		overrideBorderRadius: { type: String, default: null },
		enableComparison: { type: Boolean, default: true },
		hasPrev: { type: Boolean, default: false },
		hasNext: { type: Boolean, default: false },
	},
	mixins: [
		RunnitsImageSelectMixin,
	],
	data () {
		return {
			NODE_RUN_RESULT_SELECTION_MODE,
			WIDTH_XS_BREAKPOINT: 50,
			timerStartAtMoment: moment(),

			srcIsFetching: false,
			src: null,
			srcIsLoaded: false,
			srcIsError: false,
			unsupportedFormat: null,

			// Observed dimensions
			dimensions: {
				width: 0,
				height: 0,
			},

			// Fullscreen
			isFullScreen: false,

			// VueZoomers
			zoomed: false,
			renderZoomViewer: true,
			fullscreenZoomed: false,
			renderFullscreenZoomer: true,

			// Lazy loading
			isVisible: false,
			observer: null,

			dragTimeout: null,

			// Comparison slider state
			comparisonInitialized: false,

			// Image dimensions for comparison
			mainImageDimensions: { width: 0, height: 0 },
			comparisonImageDimensions: { width: 0, height: 0 },

			// Comparison options menu
			comparisonOptionsMenu: false,
			availableComparisonOptionsForMenu: [],
			isLoadingComparisonOptions: false,
		};
	},
	mounted () {
		this.srcFetchSeq = 0;
		this.resizeObserver = new ResizeObserver(entries => {
			for (const entry of entries) {
				this.dimensions = {
					width: Math.round(entry.contentRect.width),
					height: Math.round(entry.contentRect.height),
				};
				// Emit dimensions to parent components
				this.$emit('dimensions-updated', this.dimensions);
			}
		});

		this.resizeObserver.observe(this.$refs.runnitImageContainer);

		// Setup intersection observer for lazy loading
		this.observer = new IntersectionObserver(
			(entries) => {
				entries.forEach(entry => {
					if (entry.isIntersecting) {
						this.isVisible = true;
						// Start loading the image
						if (this.nodeRunResult) {
							this.fetchSrc(this.nodeRunResult, 'nodeRunResult');
						} else if (this.upload) {
							this.fetchSrc(this.upload, 'upload');
						} else if (this.avatar) {
							this.fetchSrc(this.avatar, 'avatar');
						}
						// Unobserve after first intersection
						this.observer.unobserve(entry.target);
					}
				});
			},
			{
				rootMargin: '500px 0px', // Start loading when within 500px of viewport
				threshold: 0.01 // Trigger when 1% of the element is visible
			}
		);

		// Start observing the container
		if (this.$refs.runnitImageContainer) {
			this.observer.observe(this.$refs.runnitImageContainer);
		}
	},
	beforeDestroy () {
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
		}
		if (this.observer) {
			this.observer.disconnect();
		}
		// Clean up ESC key listeners
		window.removeEventListener('keydown', this.handleFullscreenKeydown);

		// Clear drag timeout if it exists
		if (this.dragTimeout) {
			clearTimeout(this.dragTimeout);
			this.dragTimeout = null;
		}
	},
	computed: {
		...mapState([
			'runnitState',
			'dragAndDrop',
		]),
		computedTimerStartAtMillis () {
			return this.timerStartAtMillis || this.timerStartAtMoment.valueOf();
		},
		computedWidth () {
			if (!this.width) return null;
			return typeof this.width === 'number' ? `${this.width}px` : this.width;
		},
		computedHeight () {
			if (!this.height) return null;
			return typeof this.height === 'number' ? `${this.height}px` : this.height;
		},
		imgSizeStyles () {
			if (this.width || this.height) {
				return {
					...(this.width && { maxWidth: this.computedWidth }),
					...(this.height && { maxHeight: this.computedHeight }),
				};
			}
			if (this.isThumb) {
				return {
					maxHeight: `${THUMB_SIZE}px`,
					maxWidth: `${THUMB_SIZE}px`,
				};
			}
			if (this.isAvatar) {
				return {
					maxHeight: `${AVATAR_SIZE}px`,
					maxWidth: `${AVATAR_SIZE}px`,
				};
			}
			return {
				height: this.isInsideInfoCarousel ? 'auto' : '100%',
			};
		},
		isSelectionMode () {
			if (this.gallerySelectionContext) {
				return isGallerySelectionMode(this.gallerySelectionContext, RUNNIT_NODE_RUN_RESULT_TYPE.IMG, this.noClick);
			}
			return !!(!this.noClick && this.currentSelectionState.nodeRunResultSelectionMode && this.currentSelectionState.canSelectTypes.includes(RUNNIT_NODE_RUN_RESULT_TYPE.IMG));
		},
		isSingleSelection () {
			if (this.gallerySelectionContext) {
				return isGallerySingleSelection(this.gallerySelectionContext);
			}
			return this.currentSelectionState.nodeRunResultSelectionMode === NODE_RUN_RESULT_SELECTION_MODE.SINGLE;
		},
		isMultiSelection () {
			if (this.gallerySelectionContext) {
				return isGalleryMultiSelection(this.gallerySelectionContext);
			}
			return this.currentSelectionState.nodeRunResultSelectionMode === NODE_RUN_RESULT_SELECTION_MODE.MULTIPLE;
		},
		isSelected () {
			if (this.gallerySelectionContext) {
				return isSelectedInGalleryContext(this.gallerySelectionContext, {
					nodeRunResultUuid: this.nodeRunResult?.uuid || null,
					uploadId: this.upload?.id || null,
					avatarId: typeof this.avatar === 'object' ? this.avatar?.id || null : null,
				});
			}
			return !!(
				this.currentSelectionState.selectedNodeRunResults.length &&
				this.nodeRunResult &&
				_findIndex(this.currentSelectionState.selectedNodeRunResults, (n: RunnitNodeRunResult) => (n.uuid === this.nodeRunResult.uuid)) !== -1
			) ||
				!!(
					this.currentSelectionState.selectedUploads.length &&
					this.upload &&
					_findIndex(this.currentSelectionState.selectedUploads, (u: RunnitUpload) => (u.id === this.upload.id)) !== -1
				) ||
				!!(
					this.currentSelectionState.selectedAvatars.length &&
					this.avatar &&
					_findIndex(this.currentSelectionState.selectedAvatars, (a: Avatar) => (a.id === this.avatar.id)) !== -1
				);
		},
		computedLoadingContainerPadding () {
			let padding: number = 15;

			if (this.dimensions.width < 90) {
				padding = 10;
			}

			return padding;
		},
		useComparisonSlider () {
			return !!(
				this.runnitState.imageComparisonMode &&
				this.comparisonIsPossible
			);
		},
		comparisonIsPossible () {
			return !!(
				this.availableComparisonOptions.length > 0 &&
				this.src &&
				this.enableComparison
			);
		},
		availableComparisonOptions () {
			if (!this.enableComparison || !this.runnitState || !this.runnitState.comparisonFieldSourceImageUrls) {
				return [];
			}

			// Return array of { fieldDefUuid, imageUrl, fieldLabel } objects
			const options = [];
			const comparisonUrls = this.runnitState.comparisonFieldSourceImageUrls;

			for (const [fieldDefUuid, imageUrl] of Object.entries(comparisonUrls)) {
				if (imageUrl) {
					// Try to find the field label from nodeRun if available
					let fieldLabel = `Image ${options.length + 1}`;
					if (this.nodeRun) {
						const field = findFieldByFieldDefUuid(this.nodeRun.fields, fieldDefUuid);
						if (field && field.label) {
							fieldLabel = field.label;
						}
					}

					options.push({
						fieldDefUuid,
						imageUrl,
						fieldLabel,
					});
				}
			}

			return options;
		},
		hasMultipleComparisonOptions () {
			if (this.availableComparisonOptionsForMenu.length > 1) return true;

			// Initial estimate before fetch
			if (!this.nodeRun || !this.nodeRun.inputs) return false;
			let imageCount = 0;
			for (const inputData of Object.values(this.nodeRun.inputs) as any[]) {
				if (!inputData) continue;
				if (inputData.nodeRunResults) imageCount += inputData.nodeRunResults.length;
				else if (inputData.uploads) imageCount += inputData.uploads.length;
				else if (inputData.nodeRunResult || inputData.upload || (inputData.image && (inputData.image.nodeRunResult || inputData.image.upload))) {
					imageCount += 1;
				}
			}
			return imageCount > 1;
		},
		effectiveComparisonSourceImageUrl () {
			// Get the source image URL for the currently active comparison field
			if (this.runnitState && this.runnitState.comparisonFieldDefUuid && this.runnitState.comparisonFieldSourceImageUrls) {
				return this.runnitState.comparisonFieldSourceImageUrls[this.runnitState.comparisonFieldDefUuid] || null;
			}
			return null;
		},
		canBeDragged () {
			// Don't allow dragging if the component is disabled
			if (this.disabled) {
				return false;
			}

			// Check if there's an active drop zone
			const hasActiveDropZone = this.dragAndDrop && this.dragAndDrop.activeDropZones > 0;

			// Check if we're already dragging something
			const isAlreadyDragging: boolean = !!(this.dragAndDrop && this.dragAndDrop.isDragging && this.dragAndDrop.draggedItem);

			// Don't allow starting a new drag if we're already dragging something
			if (isAlreadyDragging) {
				return false;
			}

			// Only allow dragging if there's a drop zone and we have a valid image
			return !!(this.draggable && hasActiveDropZone && this.src);
		},

		// Calculate optimal dimensions for fullscreen comparison
		fullscreenComparisonDimensions () {
			if (!this.mainImageDimensions.width || !this.comparisonImageDimensions.width) {
				// Default fallback dimensions
				return {
					width: Math.min(window.innerWidth * 0.9, 800),
					height: Math.min(window.innerHeight * 0.8, 600),
				};
			}

			const maxWidth = window.innerWidth * 0.9;
			const maxHeight = window.innerHeight * 0.8;

			// Calculate what size each image would be when constrained to the viewport
			const mainAspectRatio = this.mainImageDimensions.width / this.mainImageDimensions.height;
			const comparisonAspectRatio = this.comparisonImageDimensions.width / this.comparisonImageDimensions.height;

			// Calculate dimensions for main image when constrained to viewport
			let mainConstrainedWidth = maxWidth;
			let mainConstrainedHeight = maxWidth / mainAspectRatio;
			if (mainConstrainedHeight > maxHeight) {
				mainConstrainedHeight = maxHeight;
				mainConstrainedWidth = maxHeight * mainAspectRatio;
			}

			// Calculate dimensions for comparison image when constrained to viewport
			let comparisonConstrainedWidth = maxWidth;
			let comparisonConstrainedHeight = maxWidth / comparisonAspectRatio;
			if (comparisonConstrainedHeight > maxHeight) {
				comparisonConstrainedHeight = maxHeight;
				comparisonConstrainedWidth = maxHeight * comparisonAspectRatio;
			}

			// Use the larger of the two constrained sizes
			const optimalWidth = Math.max(mainConstrainedWidth, comparisonConstrainedWidth);
			const optimalHeight = Math.max(mainConstrainedHeight, comparisonConstrainedHeight);

			return {
				width: Math.min(optimalWidth, maxWidth),
				height: Math.min(optimalHeight, maxHeight),
			};
		},
	},
	watch: {
		srcIsLoaded: {
			immediate: false,
			handler (newVal: boolean, oldVal: boolean) {
				if (newVal && newVal !== oldVal) {
					// The <img> that is wrapped by VueZoomer needs to be re-rendered to work properly
					this.renderZoomViewer = false;
					this.renderFullscreenZoomer = false;
					this.$nextTick(() => {
						this.renderZoomViewer = true;
						this.renderFullscreenZoomer = true;
					});
				}
			},
		},
		'$store.state.triggers.imageZoomResetTrigger': {
			immediate: true,
			async handler (newVal: number, oldVal: number) {
				if (
					newVal &&
					newVal !== oldVal &&
					this.$refs.zoomer
				) {
					this.onResetZoom();
				}
				if (
					newVal &&
					newVal !== oldVal &&
					this.$refs.fullscreenZoomer
				) {
					this.onFullscreenResetZoom();
				}
			},
		},
		isFullScreen (newVal) {
			if (newVal) {
				// Add ESC key listener when fullscreen opens - don't use capture to allow parent dialogs to handle ESC first
				window.addEventListener('keydown', this.handleFullscreenKeydown);
				this.fetchComparisonOptions();
			} else {
				// Remove ESC key listener when fullscreen closes
				window.removeEventListener('keydown', this.handleFullscreenKeydown);
			}
		},

		nodeRunResult: {
			immediate: true,
			async handler (newVal: RunnitNodeRunResult, oldVal: RunnitNodeRunResult) {
				const newFileName: string = this._get(newVal, 'file.name') || null;
				const oldFileName: string = this._get(oldVal, 'file.name') || null;
				if (newVal && newFileName !== oldFileName && this.isVisible) {
					// Reset transient per-image state. Previously a `:key` on the parent
					// caused a full remount; now navigation reuses the instance, so reset
					// zoom + cached dimensions explicitly.
					if (oldVal) {
						this.zoomed = false;
						this.fullscreenZoomed = false;
						this.mainImageDimensions = { width: 0, height: 0 };
						this.comparisonImageDimensions = { width: 0, height: 0 };
						this.comparisonOptionsMenu = false;
					}
					this.timerStartAtMoment = moment();
					await this.fetchSrc(newVal, 'nodeRunResult');
					if (this.isFullScreen) {
						this.loadImageDimensions();
						this.fetchComparisonOptions();
					}
				}
			},
		},
		upload: {
			immediate: true,
			async handler (newVal: RunnitUpload, oldVal: RunnitUpload) {
				const newFileName: string = this._get(newVal, 'name') || null;
				const oldFileName: string = this._get(oldVal, 'name') || null;
				if (newVal && newFileName !== oldFileName && this.isVisible) {
					await this.fetchSrc(newVal, 'upload');
				}
			},
		},
		avatar: {
			immediate: true,
			async handler (newVal: Avatar | string, oldVal: Avatar) {
				const newFileName: string = this._get(newVal, 'name') || null;
				const oldFileName: string = this._get(oldVal, 'name') || null;
				if (newVal && newFileName !== oldFileName && this.isVisible) {
					await this.fetchSrc(newVal, 'avatar');
				}
			},
		},
	},
	methods: {
		...mapActions([
			'updateRunnitState',
			'cacheRunnitImageUrl',
			'setIsDragging',
			'setDraggedItem',
		]),
		async retryLoopFetchSrc (filePath: string, numTries: number, token?: number) {
			// Check cache first
			if (this.runnitState.imageCache[filePath]) {
				if (token === undefined || token === this.srcFetchSeq) {
					this.src = this.runnitState.imageCache[filePath];
				}
				return;
			}

			const fileRef = storage.ref(filePath);
			let count: number = 1;
			while (true) {
				try {
					const url: string = await fileRef.getDownloadURL();
					// Cache the URL
					this.cacheRunnitImageUrl({ filePath, url });
					if (token !== undefined && token !== this.srcFetchSeq) return;
					this.src = url;

					if (this.isSelectedImage) {
						await this.addLoadedImageUrlToStore(this.src);
					}
					break;
				} catch (e) {
					if (count >= numTries) throw e;

					count++;
					await new Promise(resolve => setTimeout(resolve, 250)); // wait before next retry
				}
			}
		},
		async addLoadedImageUrlToStore (src) {
			const currentSelectionState: SelectionState = {
				...this.currentSelectionState,
				selectedLoadedAssets: [
					...this.currentSelectionState.selectedLoadedAssets,
					{
						src,
						nodeRunResult: this.nodeRunResult,
					}
				]
			};

			await this.updateRunnitState({
				currentSelectionState,
			});
		},
		async fetchSrc (input: RunnitNodeRunResult | RunnitUpload | Avatar, useCase: 'nodeRunResult' | 'upload' | 'avatar') {
			// Per-call monotonic token: rapid Prev/Next navigation can fire
			// overlapping fetchSrc invocations. Capture the current sequence
			// and bail out at every async resume point if a newer call has
			// superseded us, so out-of-order resolutions don't clobber state.
			const token: number = ++this.srcFetchSeq;
			const nodeRunResult: RunnitNodeRunResult = input as RunnitNodeRunResult;
			const upload: RunnitUpload = input as RunnitUpload;
			const avatar: Avatar = input as Avatar;

			let filePath, thumbFilePath;
			if (useCase === 'upload') {
				const filePathData: { filePath: string, thumbFilePath: string } = getRunnitUploadFilePath(upload);
				filePath = filePathData.filePath;
				thumbFilePath = filePathData.thumbFilePath;
			} else if (useCase === 'nodeRunResult') {
				const filePathData: { filePath: string, thumbFilePath: string } = getRunnitNodeRunResultFilePath(nodeRunResult);
				filePath = filePathData.filePath;
				thumbFilePath = filePathData.thumbFilePath;
			} else if (useCase === 'avatar') {
				const filePathData: { filePath: string, thumbFilePath: string } = getAvatarFilePath(avatar);
				filePath = filePathData.filePath;
				thumbFilePath = filePathData.thumbFilePath;
			}

			if (
				(
					useCase === 'nodeRunResult' &&
					nodeRunResult &&
					nodeRunResult.file &&
					nodeRunResult.file.name
				) ||
				(
					useCase === 'upload' &&
					upload &&
					upload.name
				) ||
				(
					useCase === 'avatar' &&
					avatar &&
					avatar.name
				)
			) {
				try {
					this.src = null;
					this.srcIsFetching = true;
					this.srcIsError = false;
					this.srcIsLoaded = false;
					this.unsupportedFormat = null;

					// Check if file has unsupported format
					let filename: string = '';
					if (useCase === 'nodeRunResult') {
						filename = nodeRunResult.file.name;
					} else if (useCase === 'upload') {
						filename = upload.name;
					} else if (useCase === 'avatar') {
						filename = avatar.name;
					}

					const extension: string = filename.split('.').pop()?.toLowerCase() || '';
					const mimeRaw: string = (
						useCase === 'nodeRunResult'
							? nodeRunResult?.file?.contentType
							: useCase === 'upload'
								? upload?.contentType
								: avatar?.contentType
					) || '';
					const mimeBase: string = mimeRaw.toLowerCase().split(';')[0].trim();
					const isBrowserDisplayableStoredImage: boolean = [
						'image/jpeg',
						'image/png',
						'image/webp',
						'image/gif',
					].includes(mimeBase);
					// Firestore may still carry a pre-conversion extension (e.g. .heic) while Storage holds JPEG.
					if (
						!isBrowserDisplayableStoredImage &&
						extension &&
						['tiff', 'tif', 'heic', 'heif'].includes(extension)
					) {
						this.unsupportedFormat = extension;
						this.srcIsError = true;
						return;
					}

					if (this.isThumb || this.isAvatar) {
						try {
							await this.retryLoopFetchSrc(thumbFilePath, 1, token);
						} catch (e) {
							// The thumb img wasn't created yet, so just default to the original
							await this.retryLoopFetchSrc(filePath, 10, token);
						}
					} else {
						await this.retryLoopFetchSrc(filePath, 10, token);
					}
				} catch (e) {
					console.error(e);
					if (token === this.srcFetchSeq) this.srcIsError = true;
				} finally {
					if (token === this.srcFetchSeq) this.srcIsFetching = false;
				}
			} else if (useCase === 'nodeRunResult' && nodeRunResult) {
				// The result exists but has no usable file (e.g. the provider returned
				// no file_name and the bytes were never persisted to storage), so there
				// is nothing to fetch. Show the error placeholder instead of leaving a
				// blank/black square.
				if (token === this.srcFetchSeq) {
					this.src = null;
					this.srcIsFetching = false;
					this.srcIsError = true;
				}
			}
		},
		onSrcLoaded () {
			this.srcIsLoaded = true;
		},
		onClick () {
			if (this.isSelectionMode) {
				// TODO the value emitted from this isn't actually used.
				if (this.isSingleSelection) {
					this.$emit('on-single-select', {
						nodeRunResult: this.nodeRunResult,
						upload: this.upload,
						avatar: this.avatar,
					});
				} else if (this.isMultiSelection) {
					this.$emit('on-multi-select', {
						nodeRunResult: this.nodeRunResult,
						upload: this.upload,
						avatar: this.avatar,
					});
				}
			} else {
				this.$emit('on-click');
			}
		},
		onClearClick () {
			this.$emit('on-clear');
		},
		onZoomIn () {
			this.$refs.zoomer.zoomIn();
		},
		onResetZoom () {
			this.zoomed = false;
			this.$refs.zoomer.reset();
		},
		onZoomOut () {
			this.$refs.zoomer.zoomOut();
		},
		onFullscreenZoomIn () {
			this.$refs.fullscreenZoomer.zoomIn();
		},
		onFullscreenResetZoom () {
			this.fullscreenZoomed = false;
			this.$refs.fullscreenZoomer.reset();
		},
		onFullscreenZoomOut () {
			this.$refs.fullscreenZoomer.zoomOut();
		},
		loadImageDimensions () {
			// Capture the URL bound to each Image() and bail in onload if the
			// component has since navigated to a different src. Without this,
			// rapid Prev/Next can let an older onload resolve last and write
			// the previous image's dimensions over the current one.
			if (this.src) {
				const expectedMainSrc: string = this.src;
				const mainImg = new Image();
				mainImg.onload = () => {
					if (this.src !== expectedMainSrc) return;
					this.mainImageDimensions = {
						width: mainImg.naturalWidth,
						height: mainImg.naturalHeight,
					};
				};
				mainImg.src = expectedMainSrc;
			}

			if (this.effectiveComparisonSourceImageUrl) {
				const expectedComparisonSrc: string = this.effectiveComparisonSourceImageUrl;
				const comparisonImg = new Image();
				comparisonImg.onload = () => {
					if (this.effectiveComparisonSourceImageUrl !== expectedComparisonSrc) return;
					this.comparisonImageDimensions = {
						width: comparisonImg.naturalWidth,
						height: comparisonImg.naturalHeight,
					};
				};
				comparisonImg.src = expectedComparisonSrc;
			}
		},
		openFullScreen () {
			this.isFullScreen = true;
			// Load image dimensions when opening fullscreen for better comparison sizing
			this.loadImageDimensions();
		},
		closeFullScreen () {
			this.isFullScreen = false;
			this.comparisonOptionsMenu = false; // Close options menu
			// Clear comparison mode when exiting fullscreen
			this.updateRunnitState({
				imageComparisonMode: false,
				comparisonFieldDefUuid: null,
			});
		},
		onFullScreenPrev () {
			if (!this.hasPrev) return;
			this.$emit('prev-result');
		},
		onFullScreenNext () {
			if (!this.hasNext) return;
			this.$emit('next-result');
		},
		handleFullscreenKeydown (event) {
			// Only handle ESC if our fullscreen dialog is open and visible
			if ((event.key === 'Escape' || event.keyCode === 27) && this.isFullScreen) {
				// Check if our fullscreen dialog is currently visible and active
				const ourFullscreenDialog = document.querySelector('.fullscreen-card') as HTMLElement;
				if (ourFullscreenDialog && ourFullscreenDialog.offsetParent !== null) {
					// Our fullscreen dialog is visible, so handle the ESC
					event.preventDefault();
					event.stopPropagation();
					this.closeFullScreen();
				}
				// If our dialog is not visible, let the event bubble up to parent dialogs
			}
		},
		async fetchComparisonOptions () {
			if (!this.nodeRun || !(this.nodeRun as any).inputs) return;

			this.isLoadingComparisonOptions = true;
			const options: any[] = [];

			try {
				const inputs = (this.nodeRun as any).inputs;
				for (const fieldDefUuid in inputs) {
					const inputData = inputs[fieldDefUuid] as any;
					if (!inputData) continue;

					const field = findFieldByFieldDefUuid((this.nodeRun as any).fields, fieldDefUuid);
					const fieldLabel = (field && field.label) || 'Image';

					// Handle nodeRunResults and uploads (IMGS)
					if (inputData.nodeRunResults || inputData.uploads) {
						if (inputData.nodeRunResults) {
							for (let i = 0; i < inputData.nodeRunResults.length; i++) {
								const res = inputData.nodeRunResults[i];
								const url = await this.getComparisonImageUrl(res, null);
								if (url) {
									options.push({
										fieldDefUuid,
										imageUrl: url,
										fieldLabel: `${fieldLabel} - Result ${i + 1}`,
									});
								}
							}
						}
						if (inputData.uploads) {
							for (let i = 0; i < inputData.uploads.length; i++) {
								const up = inputData.uploads[i];
								const url = await this.getComparisonImageUrl(null, up);
								if (url) {
									options.push({
										fieldDefUuid,
										imageUrl: url,
										fieldLabel: `${fieldLabel} - Upload ${i + 1}`,
									});
								}
							}
						}
					} else {
						// Handle single image fields
						let nodeRunResult = inputData.nodeRunResult;
						let upload = inputData.upload;

						if (inputData.image) {
							nodeRunResult = nodeRunResult || inputData.image.nodeRunResult;
							upload = upload || inputData.image.upload;
						}

						if (nodeRunResult || upload) {
							const url = await this.getComparisonImageUrl(nodeRunResult, upload);
							if (url) {
								options.push({
									fieldDefUuid,
									imageUrl: url,
									fieldLabel,
								});
							}
						}
					}
				}
				this.availableComparisonOptionsForMenu = options;
			} catch (e) {
				console.error('Error fetching comparison options:', e);
			} finally {
				this.isLoadingComparisonOptions = false;
			}
		},
		async getComparisonImageUrl (nodeRunResult, upload) {
			let filePath: string;

			if (nodeRunResult) {
				const filePathData = getRunnitNodeRunResultFilePath(nodeRunResult);
				filePath = filePathData.filePath;
			} else if (upload) {
				const filePathData = getRunnitUploadFilePath(upload);
				filePath = filePathData.filePath;
			} else {
				return null;
			}

			try {
				const fileRef = storage.ref(filePath);
				return await fileRef.getDownloadURL();
			} catch (e) {
				return null;
			}
		},
		async toggleComparisonMode () {
			const state = this.runnitState as any;
			if (state?.imageComparisonMode) {
				await this.updateRunnitState({
					imageComparisonMode: false,
					comparisonFieldDefUuid: null,
				});
			} else {
				await this.fetchComparisonOptions();
				if (this.availableComparisonOptionsForMenu.length > 1) {
					this.comparisonOptionsMenu = true;
				} else if (this.availableComparisonOptionsForMenu.length === 1) {
					await this.selectComparisonOption(this.availableComparisonOptionsForMenu[0]);
				}
			}
		},
		async selectComparisonOption (option) {
			await this.updateRunnitState({
				imageComparisonMode: true,
				comparisonFieldDefUuid: option.fieldDefUuid,
				comparisonFieldSourceImageUrls: {
					...(this.runnitState as any)?.comparisonFieldSourceImageUrls || {},
					[option.fieldDefUuid]: option.imageUrl,
				},
			});

			// Close the options menu
			this.comparisonOptionsMenu = false;

			// Load dimensions when entering comparison mode for better sizing
			if (this.isFullScreen) {
				this.loadImageDimensions();
			}
		},
		handleDragStart (ev) {
			if (!this.canBeDragged) {
				return;
			}

			// Create a custom drag image instead of using the full image
			const dragPreview = document.createElement('div');
			dragPreview.style.width = '100px';
			dragPreview.style.height = '100px';
			dragPreview.style.borderRadius = '6px';
			dragPreview.style.overflow = 'hidden';
			dragPreview.style.position = 'absolute';
			dragPreview.style.top = '-1000px'; // Position off-screen

			const previewImg = document.createElement('img');
			previewImg.src = this.src || '';
			previewImg.style.width = '100%';
			previewImg.style.height = '100%';
			previewImg.style.objectFit = 'cover';

			dragPreview.appendChild(previewImg);
			document.body.appendChild(dragPreview);

			// Use the custom element as drag image
			ev.dataTransfer.setDragImage(dragPreview, 50, 50);

			// Clean up the element after drag starts
			setTimeout(() => {
				document.body.removeChild(dragPreview);
			}, 0);

			// Set up the drag data
			const dragData: any = {
				type: 'RUNNIT_IMAGE',
				src: this.src
			};

			// If this is a node run result, include that data
			if (this.nodeRunResult) {
				dragData.nodeRunResult = this.nodeRunResult;
			}

			// If this is an upload, include that data
			if (this.upload) {
				dragData.upload = this.upload;
			}

			// If this is an avatar, include that data
			if (this.avatar) {
				dragData.avatar = this.avatar;
			}

			// Store the drag data in the Vuex store
			this.setDraggedItem(dragData);
			this.setIsDragging(true);

			// Set a timeout to reset the drag state if the drop event doesn't fire
			this.dragTimeout = setTimeout(() => {
				this.setIsDragging(false);
				this.setDraggedItem(null);
				document.body.style.cursor = ''; // Reset cursor
			}, 15000); // 15 seconds timeout
		},
		handleDragEnd (event) {
			// Reset cursor
			document.body.style.cursor = '';

			// Clear the timeout
			if (this.dragTimeout) {
				clearTimeout(this.dragTimeout);
				this.dragTimeout = null;
			}

			// Reset the drag state
			this.setIsDragging(false);
			this.setDraggedItem(null);
		},
	},
	components: {
		RunnitImageTimer,
		VZoomer: VueZoomer.Zoomer,
	},
});
